import type { GameState } from './gameState'
import {
  PROTOTYPE_CONSTRUCTION_CAP,
  queueFacilityConstruction,
} from './construction'
import {
  getResearchAvailability,
  startResearch,
} from './research'
import {
  getSpaceMissionAvailability,
  startSpaceMission,
} from './spaceProgram'
import type { FacilityId } from '../data/facilities'
import {
  SPACE_MISSION_DEFINITIONS,
  type SpaceMissionId,
} from '../data/spaceMissions'
import type { TechnologyId } from '../data/technologies'
import type { ActiveMajorPowerId } from '../data/regions'
import { getNationDefinition } from '../data/nations'
import {
  getNationProfile,
  type NationConstructionPlanItem,
} from '../data/nationProfiles'
import type { AiDifficulty } from './gameSettings'

export type StrategicAiAction =
  | {
      type: 'research-start'
      nationId: ActiveMajorPowerId
      technologyId: TechnologyId
    }
  | {
      type: 'construction-start'
      nationId: ActiveMajorPowerId
      regionId: string
      facilityId: FacilityId
    }
  | {
      type: 'space-mission-start'
      nationId: ActiveMajorPowerId
      missionId: SpaceMissionId
    }

export interface StrategicAiTurnResult {
  state: GameState
  actions: StrategicAiAction[]
}

export function getOpponentMajorPowerId(
  playerNationId: ActiveMajorPowerId | null,
): ActiveMajorPowerId | null {
  if (playerNationId === 'starsea') {
    return 'redplain'
  }

  if (playerNationId === 'redplain') {
    return 'starsea'
  }

  return null
}

export function applyStrategicAiDecisions(
  state: GameState,
): StrategicAiTurnResult {
  const aiNationId = getOpponentMajorPowerId(state.playerNationId)

  if (
    state.status !== 'running' ||
    aiNationId === null ||
    !getNationDefinition(aiNationId).ai.enabled
  ) {
    return {
      state,
      actions: [],
    }
  }

  const difficulty = state.settings.aiDifficulty

  if (difficulty === 'relaxed' && state.turn % 2 === 0) {
    return {
      state,
      actions: [],
    }
  }

  let nextState = state
  const actions: StrategicAiAction[] = []

  const researchResult = chooseResearch(
    nextState,
    aiNationId,
    difficulty,
  )
  nextState = researchResult.state
  if (researchResult.action) {
    actions.push(researchResult.action)
  }

  const missionResult = chooseSpaceMission(
    nextState,
    aiNationId,
    difficulty,
  )
  nextState = missionResult.state
  if (missionResult.action) {
    actions.push(missionResult.action)
  }

  if (!missionResult.waitingForResources) {
    const constructionResult = chooseConstruction(
      nextState,
      aiNationId,
      difficulty,
    )
    nextState = constructionResult.state
    actions.push(...constructionResult.actions)
  }

  return {
    state: nextState,
    actions,
  }
}

function chooseResearch(
  state: GameState,
  nationId: ActiveMajorPowerId,
  difficulty: AiDifficulty,
): {
  state: GameState
  action: StrategicAiAction | null
} {
  if (state.research[nationId].active !== null) {
    return { state, action: null }
  }

  const profile = getNationProfile(nationId)
  const priorities =
    difficulty === 'intense'
      ? profile.aiIntenseResearchPriorities
      : profile.aiResearchPriorities

  for (const technologyId of priorities) {
    const availability = getResearchAvailability(
      state.research[nationId],
      technologyId,
    )

    if (!availability.allowed) {
      continue
    }

    const result = startResearch(
      state.research[nationId],
      technologyId,
    )

    if (!result.started) {
      continue
    }

    return {
      state: {
        ...state,
        research: {
          ...state.research,
          [nationId]: result.state,
        },
      },
      action: {
        type: 'research-start',
        nationId,
        technologyId,
      },
    }
  }

  return { state, action: null }
}

function chooseSpaceMission(
  state: GameState,
  nationId: ActiveMajorPowerId,
  difficulty: AiDifficulty,
): {
  state: GameState
  action: StrategicAiAction | null
  waitingForResources: boolean
} {
  if (state.spaceProgram[nationId].active !== null) {
    return {
      state,
      action: null,
      waitingForResources: false,
    }
  }

  const mission = SPACE_MISSION_DEFINITIONS.find(
    (definition) =>
      !state.spaceProgram[nationId].completed.includes(definition.id),
  )

  if (!mission) {
    return {
      state,
      action: null,
      waitingForResources: false,
    }
  }

  const nation = state.nations[nationId]
  const availability = getSpaceMissionAvailability(
    state.spaceProgram[nationId],
    state.research[nationId],
    nation.economy.stockpile,
    mission.id,
    state.status === 'running',
  )

  if (!availability.allowed) {
    const intenseReserve =
      difficulty === 'intense' &&
      availability.reason === 'missing-technologies' &&
      availability.missingTechnologies.length <= 1 &&
      !canAfford(nation.economy.stockpile, mission.cost)

    return {
      state,
      action: null,
      waitingForResources:
        availability.reason === 'insufficient-resources' ||
        intenseReserve,
    }
  }

  const result = startSpaceMission(
    nationId,
    state.spaceProgram[nationId],
    state.research[nationId],
    nation.economy.stockpile,
    mission.id,
    true,
  )

  if (!result.started) {
    return {
      state,
      action: null,
      waitingForResources:
        result.reason === 'insufficient-resources',
    }
  }

  return {
    state: {
      ...state,
      nations: {
        ...state.nations,
        [nationId]: {
          ...nation,
          economy: {
            ...nation.economy,
            stockpile: result.stockpile,
          },
        },
      },
      spaceProgram: {
        ...state.spaceProgram,
        [nationId]: result.program,
      },
    },
    action: {
      type: 'space-mission-start',
      nationId,
      missionId: mission.id,
    },
    waitingForResources: false,
  }
}

function chooseConstruction(
  state: GameState,
  nationId: ActiveMajorPowerId,
  difficulty: AiDifficulty,
): {
  state: GameState
  actions: StrategicAiAction[]
} {
  let nextState = state
  const actions: StrategicAiAction[] = []
  const profile = getNationProfile(nationId)
  const plan =
    difficulty === 'intense'
      ? profile.aiIntenseConstructionPlan
      : profile.aiConstructionPlan
  const targetConcurrent =
    difficulty === 'relaxed' ? 1 : PROTOTYPE_CONSTRUCTION_CAP

  for (const planItem of plan) {
    if (
      countNationConstruction(nextState, nationId) >=
      targetConcurrent
    ) {
      break
    }

    if (hasFacilityOrProject(nextState, planItem)) {
      continue
    }

    const result = queueFacilityConstruction(
      nextState,
      nationId,
      planItem.regionId,
      planItem.facilityId,
    )

    if (!result.queued) {
      continue
    }

    nextState = result.state
    actions.push({
      type: 'construction-start',
      nationId,
      regionId: planItem.regionId,
      facilityId: planItem.facilityId,
    })
  }

  return {
    state: nextState,
    actions,
  }
}

function countNationConstruction(
  state: GameState,
  nationId: ActiveMajorPowerId,
): number {
  return state.construction.queue.filter(
    (project) => project.nationId === nationId,
  ).length
}

function hasFacilityOrProject(
  state: GameState,
  planItem: NationConstructionPlanItem,
): boolean {
  if (
    state.construction.facilities[planItem.regionId]?.includes(
      planItem.facilityId,
    )
  ) {
    return true
  }

  return state.construction.queue.some(
    (project) =>
      project.regionId === planItem.regionId &&
      project.facilityId === planItem.facilityId,
  )
}

function canAfford(
  stockpile: {
    money: number
    metal: number
    fuel: number
    electronics: number
  },
  cost: {
    money: number
    metal: number
    fuel: number
    electronics: number
  },
): boolean {
  return (
    stockpile.money >= cost.money &&
    stockpile.metal >= cost.metal &&
    stockpile.fuel >= cost.fuel &&
    stockpile.electronics >= cost.electronics
  )
}
